import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';

function finite(v){const n=Number(v);return v!==null&&v!==undefined&&v!==''&&Number.isFinite(n)?n:null;}
function latest(points){return Array.isArray(points)&&points.length?points[points.length-1]:null;}
function prior(points){return Array.isArray(points)&&points.length>1?points[points.length-2]:null;}
function direction(points){
  const a=prior(points),b=latest(points);
  if(!a||!b||a.value===null||b.value===null) return 'INSUFFICIENT_DATA';
  if(b.value>a.value) return 'UP';
  if(b.value<a.value) return 'DOWN';
  return 'FLAT';
}
function acceleration(points){
  if(!Array.isArray(points)||points.length<3) return 'INSUFFICIENT_DATA';
  const a=points[points.length-3]?.yoyPct,b=points[points.length-2]?.yoyPct,c=points[points.length-1]?.yoyPct;
  if([a,b,c].some(x=>finite(x)===null)) return 'INSUFFICIENT_DATA';
  if(c>b && b>=a) return 'ACCELERATING';
  if(c<b && b<=a) return 'DECELERATING';
  return 'MIXED';
}
function marginSeries(history,numeratorKey,denominatorKey){
  const n=history?.metrics?.[numeratorKey]?.points||[];
  const d=history?.metrics?.[denominatorKey]?.points||[];
  const dm=new Map(d.map(x=>[x.fy,x]));
  const out=[];
  for(const a of n){
    const b=dm.get(a.fy);
    if(!b) continue;
    const av=finite(a.value),bv=finite(b.value);
    if(av===null||bv===null||bv===0) continue;
    out.push({fy:a.fy,value:(av/bv)*100,filed:[a.filed,b.filed].filter(Boolean).sort().pop()||null,
      derivedFrom:[numeratorKey,denominatorKey]});
  }
  return out.sort((a,b)=>a.fy-b.fy);
}
function shareDilution(history){
  const p=history?.metrics?.dilutedShares?.points||[];
  const a=prior(p),b=latest(p);
  if(!a||!b||finite(a.value)===null||finite(b.value)===null||a.value===0) return null;
  return ((b.value-a.value)/Math.abs(a.value))*100;
}
function build(history={}){
  const revenue=history?.metrics?.revenue?.points||[];
  const netIncome=history?.metrics?.netIncome?.points||[];
  const operatingIncome=history?.metrics?.operatingIncome?.points||[];
  const ocf=history?.metrics?.operatingCashFlow?.points||[];
  const fcf=history?.derived?.freeCashFlow?.points||[];
  const shares=history?.metrics?.dilutedShares?.points||[];

  const netMargin=marginSeries(history,'netIncome','revenue');
  const opMargin=marginSeries(history,'operatingIncome','revenue');
  const ocfMargin=marginSeries(history,'operatingCashFlow','revenue');

  const signals=Object.freeze({
    revenueDirection:direction(revenue),
    revenueAcceleration:acceleration(revenue),
    netIncomeDirection:direction(netIncome),
    operatingIncomeDirection:direction(operatingIncome),
    operatingCashFlowDirection:direction(ocf),
    freeCashFlowDirection:direction(fcf),
    netMarginDirection:direction(netMargin),
    operatingMarginDirection:direction(opMargin),
    operatingCashFlowMarginDirection:direction(ocfMargin),
    dilutedSharesDirection:direction(shares)
  });

  return Object.freeze({
    version:VERSION,
    symbol:history.symbol||null,
    calibrated:false,
    autoScore:false,
    trajectoryInputs:Object.freeze({
      growthScore:null,
      profitabilityTrendScore:null,
      cashFlowTrendScore:null,
      dilutionTrendScore:null,
      marginTrendScore:null
    }),
    factual:Object.freeze({
      revenueCagrPct:finite(history?.metrics?.revenue?.cagrPct),
      netIncomeCagrPct:finite(history?.metrics?.netIncome?.cagrPct),
      operatingIncomeCagrPct:finite(history?.metrics?.operatingIncome?.cagrPct),
      operatingCashFlowCagrPct:finite(history?.metrics?.operatingCashFlow?.cagrPct),
      freeCashFlowCagrPct:finite(history?.derived?.freeCashFlow?.cagrPct),
      latestRevenueYoYPct:finite(latest(revenue)?.yoyPct),
      latestNetIncomeYoYPct:finite(latest(netIncome)?.yoyPct),
      latestOperatingCashFlowYoYPct:finite(latest(ocf)?.yoyPct),
      latestFreeCashFlowYoYPct:finite(latest(fcf)?.yoyPct),
      latestDilutedSharesYoYPct:finite(latest(shares)?.yoyPct),
      dilutedSharesChangePct:shareDilution(history),
      latestNetMarginPct:finite(latest(netMargin)?.value),
      latestOperatingMarginPct:finite(latest(opMargin)?.value),
      latestOperatingCashFlowMarginPct:finite(latest(ocfMargin)?.value)
    }),
    signals,
    derivedSeries:Object.freeze({
      netMargin:Object.freeze(netMargin),
      operatingMargin:Object.freeze(opMargin),
      operatingCashFlowMargin:Object.freeze(ocfMargin)
    }),
    coverage:Object.freeze({
      revenueYears:revenue.length,
      netIncomeYears:netIncome.length,
      operatingIncomeYears:operatingIncome.length,
      operatingCashFlowYears:ocf.length,
      freeCashFlowYears:fcf.length,
      dilutedShareYears:shares.length
    }),
    note:'Trajectory facts and direction signals only. Thresholds and 0–10 trajectory scoring remain uncalibrated.'
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const pts=(vals)=>vals.map((v,i)=>({fy:2022+i,value:v,yoyPct:i?((v-vals[i-1])/Math.abs(vals[i-1]))*100:null,filed:`202${3+i}-02-01`}));
  const history={
    symbol:'AAA',
    metrics:{
      revenue:{points:pts([100,120,150,210]),cagrPct:28},
      netIncome:{points:pts([5,8,12,20]),cagrPct:58},
      operatingIncome:{points:pts([10,14,20,32]),cagrPct:47},
      operatingCashFlow:{points:pts([15,20,30,45]),cagrPct:44},
      dilutedShares:{points:pts([100,102,105,110]),cagrPct:3.2}
    },
    derived:{freeCashFlow:{points:pts([10,14,22,35]),cagrPct:51}}
  };
  const b=build(history);
  ok('symbol preserved',b.symbol==='AAA');
  ok('never auto scores',b.autoScore===false);
  ok('explicitly uncalibrated',b.calibrated===false);
  ok('trajectory score inputs null',Object.values(b.trajectoryInputs).every(x=>x===null));
  ok('revenue CAGR preserved',b.factual.revenueCagrPct===28);
  ok('latest revenue YoY derived',Math.abs(b.factual.latestRevenueYoYPct-40)<1e-9);
  ok('net margin derived',Math.abs(b.factual.latestNetMarginPct-(20/210*100))<1e-9);
  ok('operating margin derived',Math.abs(b.factual.latestOperatingMarginPct-(32/210*100))<1e-9);
  ok('OCF margin derived',Math.abs(b.factual.latestOperatingCashFlowMarginPct-(45/210*100))<1e-9);
  ok('share change derived',Math.abs(b.factual.dilutedSharesChangePct-(5/105*100))<1e-9);
  ok('revenue direction up',b.signals.revenueDirection==='UP');
  ok('net income direction up',b.signals.netIncomeDirection==='UP');
  ok('FCF direction up',b.signals.freeCashFlowDirection==='UP');
  ok('shares direction up',b.signals.dilutedSharesDirection==='UP');
  ok('revenue acceleration recognized',b.signals.revenueAcceleration==='ACCELERATING');
  ok('margin series has aligned years',b.derivedSeries.netMargin.length===4);
  ok('coverage retained',b.coverage.revenueYears===4);

  const sparse=build({symbol:'X',metrics:{revenue:{points:[{fy:2025,value:10,yoyPct:null}],cagrPct:null}},derived:{freeCashFlow:{points:[],cagrPct:null}}});
  ok('sparse direction insufficient',sparse.signals.revenueDirection==='INSUFFICIENT_DATA');
  ok('sparse acceleration insufficient',sparse.signals.revenueAcceleration==='INSUFFICIENT_DATA');
  ok('missing margin remains null',sparse.factual.latestNetMarginPct===null);
  ok('missing share dilution remains null',sparse.factual.dilutedSharesChangePct===null);

  const down=direction([{fy:2024,value:10},{fy:2025,value:8}]);
  ok('down direction works',down==='DOWN');
  const flat=direction([{fy:2024,value:10},{fy:2025,value:10}]);
  ok('flat direction works',flat==='FLAT');

  const api={VERSION,build,direction,acceleration,marginSeries};
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));
  ok('no universal score',!('score' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}
g.ZooInvestSECGrowthTrajectoryInputBridge=Object.freeze({VERSION,build,direction,acceleration,marginSeries,selfTest});
})(engineContext);